/**
 * Keyboard Shortcuts Help Modal
 *
 * Shows available keyboard shortcuts organized by category.
 * Activated with "?" key.
 */

import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Keyboard } from "lucide-react";
import { cn } from "@/lib/utils";

interface Shortcut {
  key: string;
  description: string;
  category: string;
}

const shortcuts: Shortcut[] = [
  // Command Palette
  { key: "⌘K", description: "Open Command Palette", category: "Global" },

  // Navigation
  { key: "G", description: "Go to Dashboard", category: "Navigation" },
  { key: "T", description: "Go to Transactions", category: "Navigation" },
  { key: "D", description: "Go to Documents", category: "Navigation" },
  { key: "R", description: "Go to Reports", category: "Navigation" },
  { key: "V", description: "Go to VAT", category: "Navigation" },

  // Actions
  { key: "⇧N", description: "New Transaction", category: "Actions" },
  { key: "⇧I", description: "Import Data", category: "Actions" },
  { key: "⇧U", description: "Upload Documents", category: "Actions" },

  // UI
  { key: "/", description: "Focus Search", category: "UI" },
  { key: "?", description: "Show Shortcuts", category: "UI" },
  { key: "Esc", description: "Close Modal/Dialog", category: "UI" },
];

function ShortcutKey({ keys }: { keys: string }) {
  return (
    <kbd
      className={cn(
        "inline-flex items-center justify-center",
        "min-w-[1.5rem] px-1.5 py-0.5",
        "text-xs font-medium",
        "bg-muted border border-border rounded",
        "shadow-sm"
      )}
    >
      {keys}
    </kbd>
  );
}

interface KeyboardShortcutsHelpProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function KeyboardShortcutsHelp({
  open: controlledOpen,
  onOpenChange,
}: KeyboardShortcutsHelpProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  // Listen for "?" key to open help
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if in input/textarea
      const activeElement = document.activeElement;
      if (
        activeElement?.tagName === "INPUT" ||
        activeElement?.tagName === "TEXTAREA"
      ) {
        return;
      }

      // Check for existing open dialogs (except this one)
      const hasOtherDialog = document.querySelector(
        '[role="dialog"][data-state="open"]:not([data-shortcuts-help])'
      );
      if (hasOtherDialog) return;

      if (e.key === "?" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setOpen(!isOpen);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, setOpen]);

  // Group shortcuts by category
  const groupedShortcuts = shortcuts.reduce(
    (acc, shortcut) => {
      if (!acc[shortcut.category]) {
        acc[shortcut.category] = [];
      }
      acc[shortcut.category].push(shortcut);
      return acc;
    },
    {} as Record<string, Shortcut[]>
  );

  const categoryOrder = ["Global", "Navigation", "Actions", "UI"];
  const sortedCategories = Object.keys(groupedShortcuts).sort(
    (a, b) => categoryOrder.indexOf(a) - categoryOrder.indexOf(b)
  );

  return (
    <Dialog open={isOpen} onOpenChange={setOpen}>
      <DialogContent className="max-w-md" data-shortcuts-help>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Keyboard className="h-5 w-5" />
            Keyboard Shortcuts
          </DialogTitle>
          <DialogDescription>
            Navigate faster with keyboard shortcuts
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {sortedCategories.map(category => (
            <div key={category}>
              <h4 className="text-sm font-medium text-muted-foreground mb-3">
                {category}
              </h4>
              <div className="space-y-2">
                {groupedShortcuts[category].map(shortcut => (
                  <div
                    key={shortcut.key + shortcut.description}
                    className="flex items-center justify-between py-1"
                  >
                    <span className="text-sm">{shortcut.description}</span>
                    <ShortcutKey keys={shortcut.key} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="border-t pt-4">
          <p className="text-xs text-muted-foreground text-center">
            Press <ShortcutKey keys="?" /> anytime to show this help
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default KeyboardShortcutsHelp;
